<template>
    <div class="w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2 px-2 flex flex-col items-center gap-1">
        <BaseButton theme="secondary" data-testid="draft-self-review" :disabled="loading" class="flex items-center justify-center gap-2" @click="handleClick">
            <LoadingSpinner v-if="loading" class="h-4" />
            <i v-else class="bi bi-workspace" aria-hidden="true"></i>
            Draft my review for Workday
        </BaseButton>
        <div v-if="noticeMessage" class="body-small text-red-700" role="alert">{{ noticeMessage }}</div>

        <ChatSelfReviewDialog v-model:open="dialogOpen" :draft="draft" :message-id="latestMessageId" />
    </div>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { inject, ref } from "vue";

import ChatSelfReviewDialog from "./ChatSelfReviewDialog.vue";

interface SelfReviewDraft {
    goals_comment: string;
    values_comment: string;
    performance_comment: string;
    overall_comment: string;
}

interface Props {
    latestMessageId: number;
}

const props = defineProps<Props>();

const { execute } = useActionHandler();
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const noticeMessage = ref("");
const dialogOpen = ref(false);
const draft = ref<SelfReviewDraft | null>(null);

async function handleClick() {
    startLoading();
    noticeMessage.value = "";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}draft_self_review_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        const result = await execute(ChatAction.DRAFT_SELF_REVIEW_CARD, { message_id: props.latestMessageId }, { sync: true });
        if (result?.success && result.payload?.draft) {
            draft.value = result.payload.draft;
            dialogOpen.value = true;
        } else {
            noticeMessage.value = result?.payload?.error ?? "We couldn't draft your review. Please try again.";
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = "We couldn't draft your review. Please try again.";
    } finally {
        stopLoading();
    }
}
</script>
